/**
 * South African ID Number Validation Utility
 *
 * Validates South African ID numbers according to the official format:
 * - 13 digits total
 * - Digits 1-6: Date of birth (YYMMDD)
 * - Digits 7-10: Gender sequence (0000-4999 female, 5000-9999 male)
 * - Digit 11: Citizenship (0 = SA citizen, 1 = permanent resident)
 * - Digit 12: Usually 8 (legacy/administrative)
 * - Digit 13: Checksum digit (calculated using Luhn algorithm)
 */

/**
 * Validates if a date is valid given year, month, day
 */
function isValidDate(year, month, day) {
  // Convert 2-digit year to 4-digit year
  const fullYear = year < 50 ? 2000 + year : 1900 + year;

  const date = new Date(fullYear, month - 1, day);
  return (
    date.getFullYear() === fullYear &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * Calculates the Luhn checksum for South African ID numbers
 * Note: SA uses a modified Luhn algorithm processing from left to right
 */
function calculateLuhnChecksum(digits) {
  const arr = digits.split("").map(Number);
  let sum = 0;

  // Process from left to right
  for (let i = 0; i < arr.length; i++) {
    let digit = arr[i];

    // Double every second digit (index 1, 3, 5, 7, 9, 11)
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
  }

  return (10 - (sum % 10)) % 10;
}

/**
 * Validates a South African ID number
 * @param {string} idNumber - The ID number to validate
 * @returns {Object} Validation result with details
 */
export function validateSouthAfricanId(idNumber) {
  const result = {
    isValid: false,
    errors: [],
    details: null,
  };

  // Skip validation in staging environment
  if (import.meta.env?.VITE_ENVIRONMENT === "staging") {
    result.isValid = true;
    result.details = {
      dateOfBirth: {
        year: 2000,
        month: 1,
        day: 1
      },
      gender: 'male',
      citizenship: 'South African citizen',
      administrativeDigit: 8,
      checksumDigit: 0
    };
    return result;
  }

  // Basic format check
  if (!idNumber) {
    result.errors.push("ID number is required");
    return result;
  }

  const cleanId = idNumber.toString().replace(/\D/g, "");

  if (cleanId.length !== 13) {
    result.errors.push("ID number must be exactly 13 digits");
    return result;
  }

  // Extract components
  const year = parseInt(cleanId.substring(0, 2), 10);
  const month = parseInt(cleanId.substring(2, 4), 10);
  const day = parseInt(cleanId.substring(4, 6), 10);
  const genderSequence = parseInt(cleanId.substring(6, 10), 10);
  const citizenship = parseInt(cleanId.substring(10, 11), 10);
  const administrativeDigit = parseInt(cleanId.substring(11, 12), 10);
  const checksumDigit = parseInt(cleanId.substring(12, 13), 10);

  // Validate date of birth
  if (!isValidDate(year, month, day)) {
    result.errors.push("Invalid date of birth in ID number");
  }

  // Validate month (01-12)
  if (month < 1 || month > 12) {
    result.errors.push("Invalid month in date of birth");
  }

  // Validate day (01-31)
  if (day < 1 || day > 31) {
    result.errors.push("Invalid day in date of birth");
  }

  // Validate gender sequence (0000-9999)
  if (genderSequence < 0 || genderSequence > 9999) {
    result.errors.push("Invalid gender sequence");
  }

  // Validate citizenship digit (0 or 1)
  if (citizenship !== 0 && citizenship !== 1) {
    result.errors.push("Invalid citizenship digit (must be 0 or 1)");
  }

  // Validate checksum
  const calculatedChecksum = calculateLuhnChecksum(cleanId.substring(0, 12));
  if (calculatedChecksum !== checksumDigit) {
    result.errors.push("Invalid checksum digit");
  }

  // If no errors, mark as valid and provide details
  if (result.errors.length === 0) {
    result.isValid = true;
    result.details = {
      dateOfBirth: {
        year: year < 50 ? 2000 + year : 1900 + year,
        month: month,
        day: day,
      },
      gender: genderSequence < 5000 ? "female" : "male",
      citizenship:
        citizenship === 0 ? "South African citizen" : "Permanent resident",
      administrativeDigit: administrativeDigit,
      checksumDigit: checksumDigit,
    };
  }

  return result;
}

/**
 * Formats a South African ID number for display
 * @param {string} idNumber - The ID number to format
 * @returns {string} Formatted ID number (YYMMDD GGGG C A Z)
 */
export function formatSouthAfricanId(idNumber) {
  if (!idNumber) return "";

  const cleanId = idNumber.toString().replace(/\D/g, "");

  if (cleanId.length !== 13) return idNumber;

  return `${cleanId.substring(0, 6)} ${cleanId.substring(6, 10)} ${cleanId.substring(10, 11)} ${cleanId.substring(11, 12)} ${cleanId.substring(12, 13)}`;
}

/**
 * Extracts date of birth from a South African ID number
 * @param {string} idNumber - The ID number
 * @returns {Date|null} Date of birth or null if invalid
 */
export function extractDateOfBirth(idNumber) {
  const validation = validateSouthAfricanId(idNumber);

  if (!validation.isValid || !validation.details) return null;

  const { year, month, day } = validation.details.dateOfBirth;
  return new Date(year, month - 1, day);
}

/**
 * Extracts gender from a South African ID number
 * @param {string} idNumber - The ID number
 * @returns {string|null} 'male', 'female', or null if invalid
 */
export function extractGender(idNumber) {
  const validation = validateSouthAfricanId(idNumber);

  if (!validation.isValid || !validation.details) return null;

  return validation.details.gender;
}

/**
 * Checks if an ID number belongs to a South African citizen
 * @param {string} idNumber - The ID number
 * @returns {boolean|null} true for citizen, false for permanent resident, null if invalid
 */
export function isSouthAfricanCitizen(idNumber) {
  const validation = validateSouthAfricanId(idNumber);

  if (!validation.isValid || !validation.details) return null;

  return validation.details.citizenship === "South African citizen";
}

/**
 * React hook for ID validation with real-time feedback
 */
export function useIdValidation(idNumber) {
  const validation = validateSouthAfricanId(idNumber);

  return {
    isValid: validation.isValid,
    errors: validation.errors,
    details: validation.details,
    isEmpty: !idNumber || idNumber.trim() === "",
    isPartial:
      idNumber &&
      idNumber.replace(/\D/g, "").length > 0 &&
      idNumber.replace(/\D/g, "").length < 13,
  };
}

// Export default validation function
export default validateSouthAfricanId;
